/**
 * Frontend mirror of the backend `EmailType` enum
 * (`src/models/email-type.enum.ts`). The admin Emails filter derives its
 * dropdown from this list, so adding a type is a one-line change here and the
 * label is generated automatically — no separate label list to keep in sync.
 *
 * Keep the values in step with the backend enum. `emailTypeOptions()` builds
 * the `{ value, label }` list the dropdown binds to.
 */
export const EMAIL_TYPE_VALUES = [
  'password_reset',
  'vendor_expiring_soon',
  'vendor_expiring_today',
  'vendor_expired_1_30_days',
  'vendor_expired_31_90_days',
  'vendor_expired_91_150_days',
  'vendor_expired_151_182_days',
  'vendor_registration_invite',
  'account_created',
  'nightly_client_status',
  'weekly_client_status',
  'vendor_suspended',
  'vendor_approved',
  'vendor_reviewing',
  'vendor_registered',
  'vendor_declined',
  'client_registered_vendor_invite',
  'proposal_bid_reminder_vendor',
  'proposal_request_released',
  'proposal_vendor_submits_bid_vendor',
  'proposal_bid_cancelled_vendor',
  'proposal_bid_closed_vendor',
  'proposal_vendor_awarded_bid_vendor',
  'proposal_vendor_not_awarded_bid_vendor',
  'proposal_vendor_decline_to_bid_client',
  'proposal_vendor_submits_bid_client',
  'proposal_bid_reminder_client',
  'vendor_non_compliant',
  'vendor_awaiting_payment',
  'vendor_incomplete_registration',
  'client_confirm_pre_registration',
  'client_contract_notice',
  'client_insurance_expiring_digest'
] as const;

export type EmailTypeValue = (typeof EMAIL_TYPE_VALUES)[number];

export interface EmailTypeOption {
  value: EmailTypeValue;
  label: string;
}

const AUDIENCE_SUFFIXES = ['vendor', 'client'];

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Turns a snake_case email-type value into a human label, e.g.
 * `proposal_bid_reminder_vendor` → `Proposal Bid Reminder (Vendor)` and
 * `vendor_expired_1_30_days` → `Vendor Expired 1-30 Days`.
 */
export function emailTypeLabel(value: string): string {
  const segments = value.split('_');

  // A trailing "vendor" / "client" segment is the audience, shown as "(Vendor)".
  let audience: string | undefined;
  if (AUDIENCE_SUFFIXES.includes(segments[segments.length - 1])) {
    audience = segments.pop();
  }

  let label = segments.map(capitalize).join(' ');

  // Day ranges split into separate segments; rejoin them: "1 30" → "1-30".
  label = label.replace(/(\d+) (\d+)/g, '$1-$2');

  if (audience) {
    label += ` (${capitalize(audience)})`;
  }

  return label;
}

export function emailTypeOptions(): EmailTypeOption[] {
  return EMAIL_TYPE_VALUES.map((value) => ({
    value,
    label: emailTypeLabel(value)
  }));
}
